import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/authStore";
import { useNavigate, useLocation } from "react-router-dom";
import { TEST_IDS } from "../../constants/testIds";
import { AlertTriangle, Info, MailCheck, Menu, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import DndToggle from "./DndToggle";
import NotificationBell from "./NotificationBell";
import ScopeToggle from "./ScopeToggle";
import CommandPalette from "./CommandPalette";
import MotivationChip from "./MotivationChip";
import { SerpixWordmark } from "../brand/SerpixWordmark";

function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  return Math.max(0, Math.ceil((target - Date.now()) / (24 * 60 * 60 * 1000)));
}

// Verify banner is suppressed for 48h after the user clicks "Resend".
const VERIFY_SUPPRESS_MS = 48 * 60 * 60 * 1000;
const VERIFY_KEY = "taskora_verify_resent_at";

export default function TopBar({ onMenuOpen }) {
  const { t, i18n } = useTranslation();
  const { tenant, user, setLanguage } = useAuthStore();
  const navigate = useNavigate();
  const loc = useLocation();

  const switchLang = () => {
    const next = i18n.language === "tr" ? "en" : "tr";
    setLanguage(next);
  };

  const status = tenant?.subscription_status;
  const isSuperAdmin = ["super_admin", "ultra_admin"].includes(user?.role);

  // ---- Email verify banner ----
  const [verifySuppressed, setVerifySuppressed] = useState(() => {
    const ts = parseInt(localStorage.getItem(VERIFY_KEY) || "0", 10);
    return ts && Date.now() - ts < VERIFY_SUPPRESS_MS;
  });

  useEffect(() => {
    if (!verifySuppressed) return;
    const ts = parseInt(localStorage.getItem(VERIFY_KEY) || "0", 10);
    const remaining = ts ? VERIFY_SUPPRESS_MS - (Date.now() - ts) : 0;
    if (remaining <= 0) {
      setVerifySuppressed(false);
      return;
    }
    const timer = setTimeout(() => setVerifySuppressed(false), remaining);
    return () => clearTimeout(timer);
  }, [verifySuppressed]);

  useEffect(() => {
    if (user?.email_verified) {
      localStorage.removeItem(VERIFY_KEY);
    }
  }, [user?.email_verified]);

  const needsVerify =
    user && user.tenant_id && user.email_verified === false && !verifySuppressed;

  const resendVerify = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("auth.verify_email_sent"));
      localStorage.setItem(VERIFY_KEY, String(Date.now()));
      setVerifySuppressed(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---- Trial banner ----
  const trialDaysLeft = daysUntil(tenant?.trial_ends_at);
  const showBillingBanner =
    isSuperAdmin &&
    (status === "past_due" ||
      status === "suspended" ||
      (status === "trial" && trialDaysLeft !== null && trialDaysLeft <= 3));

  return (
    <>
      <header
        className="sticky top-0 z-20 border-b border-zinc-200 bg-white/85 backdrop-blur-xl backdrop-saturate-150"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex h-14 items-center justify-between gap-1 px-2 sm:h-16 sm:gap-2 sm:px-6 md:px-8">
        {/* Left cluster: hamburger + brand (mobile only) or tenant meta (desktop) */}
        <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={onMenuOpen}
            data-testid="topbar-menu-btn"
            aria-label="Open menu"
            className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition-colors hover:border-zinc-300 hover:text-zinc-900 md:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="md:hidden">
            <SerpixWordmark size="sm" hideTextOnMobile />
          </div>
          <div className="hidden min-w-0 md:block">
            <div className="truncate text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
              {tenant?.name || t("brand")}
            </div>
            <div className="truncate text-xs text-zinc-500">
              {tenant?.timezone || "UTC"} · {status || "—"}
            </div>
          </div>
        </div>

        {/* Right cluster — compact chips on mobile, full on desktop */}
        <div className="flex flex-none items-center gap-1 sm:gap-2 md:gap-3">
          {status === "trial" && loc.pathname !== "/app/billing" && (
            <button
              type="button"
              onClick={() => navigate("/app/billing")}
              data-testid="topbar-upgrade-btn"
              title={
                trialDaysLeft !== null
                  ? `Deneme sürenizin bitmesine ${trialDaysLeft} gün var — paralı plana geçin`
                  : "Paralı plana geçin"
              }
              className="inline-flex h-9 flex-none items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-3 text-xs font-semibold text-white shadow-sm transition-transform hover:scale-[1.03] sm:px-3.5"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Yükselt</span>
            </button>
          )}
          <div className="hidden sm:block">
            <MotivationChip />
          </div>
          <ScopeToggle />
          <CommandPalette />
          <NotificationBell />
          <DndToggle />
          <button
            data-testid={TEST_IDS.langSwitch}
            onClick={switchLang}
            className="hidden h-9 items-center rounded-full border border-zinc-200 px-2.5 text-xs font-semibold uppercase tracking-wider text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900 sm:inline-flex"
          >
            {i18n.language === "tr" ? "EN" : "TR"}
          </button>
          <div
            data-testid={TEST_IDS.topbarUserMenu}
            className="flex flex-none items-center gap-2 rounded-full border border-zinc-200 px-1 py-1 sm:px-3 sm:py-1.5"
          >
            <div className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-zinc-900 text-xs font-medium text-white">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <span className="hidden max-w-[10rem] truncate text-sm text-zinc-700 md:inline">
              {user?.name}
            </span>
          </div>
        </div>
        </div>
      </header>

      {needsVerify && (
        <div
          data-testid={TEST_IDS.verifyBanner}
          className="flex flex-wrap items-center justify-between gap-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-xs text-blue-900 sm:px-8"
        >
          <div className="flex min-w-0 items-center gap-2">
            <MailCheck className="h-3.5 w-3.5 flex-none" />
            <span className="min-w-0 break-words">{t("auth.verify_email_banner")}</span>
          </div>
          <button
            onClick={resendVerify}
            data-testid={TEST_IDS.verifyResendBtn}
            className="rounded-md border border-blue-300 bg-white px-2 py-0.5 text-xs font-medium text-blue-900 hover:bg-blue-100 transition-colors"
          >
            {t("auth.verify_email_resend")}
          </button>
        </div>
      )}

      {showBillingBanner && (
        <div
          data-testid={TEST_IDS.billingBanner}
          className={[
            "flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 text-sm sm:px-8",
            status === "trial"
              ? "border-blue-200 bg-blue-50 text-blue-900"
              : "border-amber-200 bg-amber-50 text-amber-900",
          ].join(" ")}
        >
          <div className="flex min-w-0 items-center gap-2">
            {status === "trial" ? (
              <Info className="h-4 w-4 flex-none" />
            ) : (
              <AlertTriangle className="h-4 w-4 flex-none" />
            )}
            <span className="min-w-0 break-words">
              {status === "trial" &&
                t("billing.trial_banner", { days: trialDaysLeft ?? 0 })}
              {status === "past_due" && t("billing.past_due_banner")}
              {status === "suspended" && t("billing.suspended_banner")}
            </span>
          </div>
          {loc.pathname !== "/app/billing" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/app/billing")}
              className="border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50"
            >
              {t("nav.billing")}
            </Button>
          )}
        </div>
      )}
    </>
  );
}
